import { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Avatar, type AvatarSize } from './Avatar';

/**
 * User Profile P1 — avatar + hidden file input + camera badge + remove control.
 * Extracted from the inline pattern in `UserDetailPage`, so the self-service
 * profile and the administrator editor share one upload affordance.
 *
 * The component only PICKS a file; the caller uploads. Client-side type / size
 * checks are a courtesy — the server validates again (`ParseFilePipe` and the
 * photo service) and is the authority.
 */
export const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

export function isAcceptedPhoto(file: Pick<File, 'type' | 'size'>): string | null {
  if (!/^image\/(jpeg|jpg|png|webp)$/.test(file.type))
    return 'Only JPEG, PNG or WEBP images are accepted.';
  if (file.size > PHOTO_MAX_BYTES) return 'The photo must be 5 MB or smaller.';
  return null;
}

/**
 * PP-1 — one message per server outcome for the upload / replace path
 * (PP spec §5.4). Reads the HTTP status and, when present, the structured
 * `error.code` the exception filter forwards. Shared by the self-service page
 * and the administrator editor so the copy cannot drift.
 */
export function photoUploadErrorMessage(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: unknown } } | undefined)
    ?.response;
  const status = response?.status;
  const code = (response?.data as { error?: { code?: string } } | undefined)?.error?.code;
  if (code === 'PHOTO_CONFLICT' || status === 409) {
    return 'Your photo was changed elsewhere. Reload and try again.';
  }
  if (code === 'PHOTO_STORAGE_UNAVAILABLE' || status === 503) {
    return 'Photos are temporarily unavailable. Try again shortly.';
  }
  if (status === 413) return 'The photo must be 5 MB or smaller.';
  if (code === 'PHOTO_INVALID' || status === 400) {
    return 'That image could not be used. Choose a JPEG, PNG or WEBP photo of at least 128 × 128 pixels.';
  }
  return 'The photo could not be uploaded. Please try again.';
}

export interface PhotoUploadProps {
  firstName?: string | null;
  lastName?: string | null;
  src?: string | null;
  size?: AvatarSize;
  uploading?: boolean;
  disabled?: boolean;
  /** Called with an accepted file, or with an error message for a refused one. */
  onSelect: (file: File) => void;
  onReject?: (message: string) => void;
  /** When present and a photo exists, a remove control is rendered. */
  onRemove?: () => void;
  inputId?: string;
}

export function PhotoUpload({
  firstName,
  lastName,
  src,
  size = 'xl',
  uploading = false,
  disabled = false,
  onSelect,
  onReject,
  onRemove,
  inputId = 'profile-photo-input',
}: PhotoUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const busy = uploading || disabled;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        <Avatar firstName={firstName} lastName={lastName} src={src} size={size} />
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={PHOTO_ACCEPT}
          className="sr-only"
          aria-label="Choose a profile photo"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            const problem = isAcceptedPhoto(file);
            if (problem) onReject?.(problem);
            else onSelect(file);
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          aria-label={src ? 'Change photo' : 'Upload photo'}
          className="absolute -bottom-1 -right-1 flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-foreground shadow transition-colors hover:bg-muted disabled:opacity-60 sm:h-9 sm:w-9"
        >
          <FontAwesomeIcon
            icon={uploading ? faSpinner : faCamera}
            spin={uploading}
            className="h-3.5 w-3.5"
          />
        </button>
      </div>
      {src && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="flex min-h-11 items-center gap-1.5 rounded-lg px-3 text-xs text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-60 sm:min-h-9"
        >
          <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
          Remove photo
        </button>
      )}
    </div>
  );
}
